/**
 * The video zone on a public influencer profile: one stage, and a playlist beside it.
 *
 * The switching itself — index, the aria-hidden per slide, arrow keys and touch swipe — lives in
 * carousel.js and is shared with the public project deck. The playlist rows are that carousel's
 * `dots`: they carry data-index and take aria-current, so the list and the stage cannot disagree.
 * There are no prev/next arrows, because the design offers the whole list instead.
 *
 * GESTURES STAY ON. The ticket's non-functional requirements ask for touch swipe on mobile IN
 * ADDITION to tapped controls, and for arrow-key navigation — so swiping the stage moves to the
 * next video even though the playlist is the visible control. The projects and stories zones turn
 * gestures off for reasons that do not apply here: a tab strip is a list of choices, and stories
 * nest one carousel inside another.
 *
 * What is left here is the only part that is about video: NOTHING THIRD-PARTY LOADS ON PAGE LOAD.
 * Each slide carries its source in a data attribute and stays a poster image until the visitor asks
 * for it. Six videos cost six images, not six players.
 *
 * lite-youtube is a dependency here and would have done this for YouTube alone. One facade for all
 * three sources beats one library plus a second hand-rolled path for Vimeo and uploads, which is
 * the shape that drifts.
 */

import { createCarousel } from "./carousel.js";

const CAROUSEL_SELECTOR = ".js-member-videos";

// Each stage's poster and play button while its player is mounted. They are detached rather than
// destroyed, so putting them back restores the button's click listener and the loaded poster too.
const posters = new WeakMap();

/**
 * Builds the actual player for a slide, once.
 *
 * The iframe is created here rather than rendered server-side and hidden, because a hidden iframe
 * still loads: it would fetch the player, set cookies where the provider sets them, and cost the
 * visitor the bytes for five videos they never watched.
 */
const mountPlayer = (slide) => {
  const stage = slide.querySelector(".js-member-videos__stage");

  if (!stage || stage.dataset.mounted === "true") {
    return;
  }

  const kind = slide.dataset.kind;
  const source = slide.dataset.source;

  if (!source) {
    return;
  }

  const player =
    kind === "file" ? document.createElement("video") : document.createElement("iframe");

  if (kind === "file") {
    player.src = source;
    player.controls = true;
    player.autoplay = true;
    player.setAttribute("playsinline", "");
  } else {
    player.src = source;
    player.setAttribute("frameborder", "0");
    player.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
    player.setAttribute("allowfullscreen", "");
  }

  player.className = "member-videos__player";

  posters.set(stage, [...stage.childNodes]);
  stage.replaceChildren(player);
  stage.dataset.mounted = "true";
};

/**
 * Tears a player back down when its slide is left.
 *
 * Without this, moving to the next video leaves the previous one playing out of sight — the
 * ticket's "plays inline without moving the others" only holds while exactly one is playing.
 */
const unmountPlayer = (slide) => {
  const stage = slide.querySelector(".js-member-videos__stage");

  if (!stage || stage.dataset.mounted !== "true") {
    return;
  }

  const player = stage.querySelector(".member-videos__player");

  // Pausing before removing stops audio continuing for the moment the element is still live.
  if (player?.tagName === "VIDEO") {
    player.pause();
  }

  // Puts the poster and play button back. Only removing the player left an empty black stage
  // with no way to play the video again.
  stage.replaceChildren(...(posters.get(stage) ?? []));
  delete stage.dataset.mounted;
};

const setUpCarousel = (carousel) => {
  const deck = createCarousel(carousel, {
    slideSelector: ".js-member-videos__slide",
    dotSelector: ".js-member-videos__row",

    // The CSS hides the inactive stages only once this class is on the root, so a visitor with no
    // JavaScript gets every video stacked and readable rather than one video and no way to reach
    // the rest. The zone used to hide them unconditionally, which meant exactly that.
    interactiveClass: "is-interactive",

    onLeave: unmountPlayer,
  });

  if (!deck) {
    return;
  }

  deck.slides.forEach((slide, position) => {
    slide.querySelector(".js-member-videos__play")?.addEventListener("click", () => {
      deck.show(position);
      mountPlayer(slide);
    });
  });
};

export default function initMemberProfileVideos() {
  document.querySelectorAll(CAROUSEL_SELECTOR).forEach(setUpCarousel);
}
